import { useMemo } from "react"

import { compareVariantKeys } from "shared/lib/variant-key/compareVariantKeys"

import { useProductConfigurationQueryWithCache } from "entities/dim-configuration/api/useProductConfigurationQueryWithCache"
import { useProductConfigurationModelsQuery } from "entities/dim-configuration/graphql/product-configuration.queries.generated"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"
import { isProductConfigurationValid } from "entities/dim-configuration/lib/isProductConfigurationValid"
import { getProductType } from "entities/products/lib/getProductType"
import { isVariantKey } from "entities/products/lib/isVariantKey"
import { useProductObj } from "entities/products/model/useProductObj"

import { transformProductObjToProductSearchResult } from "../lib/transformProductObjToProductSearchResult"
import { ProductSearchResult } from "../types/GlobalSearch"

interface UseVariantKeySearchProps {
  searchTerm?: string | null
}

export function useVariantKeySearch({ searchTerm }: UseVariantKeySearchProps) {
  const { data: modelsData } = useProductConfigurationModelsQuery()

  const hasModel = useMemo(() => {
    return !!modelsData?.configurationModels?.models?.some(
      model => model.id === getProductType(searchTerm)
    )
  }, [modelsData, searchTerm])

  const { data, loading: isLoading } = useProductConfigurationQueryWithCache({
    variables: getProductConfigurationQueryVariables({
      variantKey: searchTerm,
      productType: getProductType(searchTerm) as string
    }),
    skip:
      !searchTerm ||
      searchTerm.length < 2 ||
      !isVariantKey(searchTerm) ||
      !hasModel
  })

  const isValid = useMemo(() => {
    return (
      searchTerm &&
      isProductConfigurationValid(data?.productConfiguration) &&
      data?.productConfiguration?.variantKey?.startsWith(
        searchTerm.toUpperCase()
      )
    )
  }, [data, searchTerm])

  const { productObj, productBaseVariantObj } = useProductObj(
    data?.productConfiguration?.variantKey ?? "",
    {
      returnHiddenProducts: false
    }
  )
  const product = productBaseVariantObj || productObj

  const results = useMemo<ProductSearchResult[]>(() => {
    const variantKey =
      data?.productConfiguration?.variantKey?.toUpperCase() ?? ""
    return product && isValid && searchTerm
      ? [
          {
            ...transformProductObjToProductSearchResult(product),
            variantKey,
            matchInfo: {
              variantKey: {
                value: variantKey,
                ranges: compareVariantKeys(searchTerm, variantKey)?.ranges2
              }
            }
          }
        ]
      : []
  }, [product?.id(), isValid, searchTerm])

  return {
    results,
    isLoading
  }
}
